import { createSelector } from "reselect"
import { ASSET_TYPES_MAP } from "helpers/assets/constants"
import pluralize from "pluralize"
import { Work, Dns, ShoppingBasket, SelectAll, BlurOn, Tonality, Texture, Star, StarBorder, Gradient, OpenInNew, AttachMoney } from "@mui/icons-material"
import { chain, find, take, size, get, mapValues, isFunction, includes, omitBy, sum, filter, pick, toLower } from "lodash"
import { Creators as RootCreators } from "modules/portfolio-root/model"
import { Creators as SecurityProfileCreators } from "modules/security-profile/model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Types as CompositeTypes } from "model/compositeSecurity"
import { modelSupportsComposites } from "selectors/missingDataFiles"
import { getRecentInstruments } from "selectors/recentInstruments"
import { getRawTopic } from "modules/research-topic-root/selectors"
import { getCompositeSecurityTooltip } from "helpers/compositeSecurities"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"

pluralize.addPluralRule(/index$/i, "indexes")
const RECENT_RESULTS_LIMIT = 10

const DEFAULT_ITEMS_ACTIONS = {
  benchmark: {
    secondaryAction: item => () => SecurityProfileCreators.setBase(item.type, item.id, item.name),
  },
  index: {
    secondaryAction: item => () => SecurityProfileCreators.setBase(item.type, item.id, item.name),
  },
  portfolio: {
    secondaryAction: item => () => SecurityProfileCreators.setBase(item.type, item.id, item.name),
  },
  experiment: {
    secondaryAction: item => () => SecurityProfileCreators.setBase(item.type, item.id, item.name),
  },
  security: {
    mainAction: item => () => {
      if (item.assetClass === "currency") {
        return ProfileCreators.openFactorProfile(item.ticker)
      } else {
        return ProfileCreators.openSecurityProfile2(item.assetClass, item.ticker, item.ticker, item.exchange)
      }
    },
    secondaryAction: item => () => SecurityProfileCreators.setSecurityBase(item.assetClass, item.ticker, item.exchange),
  },
  factor: {
    mainAction: item => () => ProfileCreators.openFactorProfile(item.id),
    secondaryAction: item => () => RootCreators.toggleFavoriteFactor(item.id),
  },
}

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

const getRawResults = state => get(state, "modules.unifiedSearch.results")
const getSelectedRaw = state => get(state, "modules.unifiedSearch.selected")
const getHiddenTypes = (_, props) => get(props, "hiddenTypes", EMPTY_ARRAY)
const getFilterEquitiesFunction = (_, props) => get(props, "filterEquities")
const getItemsActions = (_, props) => get(props, "itemsActions", DEFAULT_ITEMS_ACTIONS)
const getRawSelectedTab = state => get(state, "modules.unifiedSearch.selectedTab")
const getRawModelId = state => get(state, "modules.unifiedSearch.modelId")
const getPropsModelId = (_, props) => get(props, "modelId")
const getFavoriteFactors = state => get(state, "modules.unifiedSearch.favoriteFactors", EMPTY_OBJECT)
const getMissingDataFiles = state => get(state, "global.data.unifiedSearchModelMetadata.data.model.missingDataFiles", EMPTY_ARRAY)
const getSearchType = (_, props) => get(props, "searchType")
const getCurrentPortfolio = (state) => get(state, "global.data.portfolio.data")
const getDashboardInstrument = (_, props) => get(props, "instrument")
const getSecurityIds = (_, props) => get(props, "securityIds")
export const getCompositeAssetSecurityIds = state => get(state, "global.data.compositeAssetSecurityIds.data")

function computeSearchResults(searchResults, hiddenTypes, filterEquities, recentResults) {
  const results = searchResults || recentResults
  if (!results) {
    return
  }

  const typesWithCounts = ["equities", "currencies"]
  const hiddenTypesWithCounts = [
    ...hiddenTypes,
    ...typesWithCounts
      .filter(type => hiddenTypes?.includes(type))
      .map(type => `${type}Count`),
  ]

  const withoutHiddenTypes = omitBy(results, (_, key) => includes(hiddenTypesWithCounts, key))
  if (!isFunction(filterEquities)) {
    return withoutHiddenTypes
  }

  const equities = filter(withoutHiddenTypes.equities, filterEquities)
  return {
    ...withoutHiddenTypes,
    equities,
    equitiesCount: size(equities),
  }
}

const getResourceForExperiments = createSelector(
  [getCurrentPortfolio, getRawTopic, getDashboardInstrument],
  (currentPortfolio, topic, dashboardInstrument) => currentPortfolio?.id || topic?.id || dashboardInstrument?.id,
)

function determineInstrumentType(instrument, resourceForExperiments) {
  const isEquity = instrument.assetClass === ASSET_TYPES_MAP.EQUITY
  const isBasket = instrument.type === ASSET_TYPES_MAP.SWAP
  const isExperiment = ["OPTIMIZATION", "SIMULATION", "SMART_TRADE"].includes(instrument.type)

  // Filter out experiments that don't match the resource
  if (isExperiment && instrument.resourceId !== resourceForExperiments) {
    return null
  }

  // Determine type based on instrument properties
  let type = isEquity ? instrument.assetClass : instrument.type
  if (isExperiment) {
    type = "experiment"
  }

  // Filter out instruments without a valid type
  if (!type) {
    return null
  }

  return { type, isEquity, isBasket, isExperiment }
}

function computeNewInstrument(instrument, isEquity, isBasket, isExperiment) {
  if (isEquity || isBasket) {
    return { ...instrument, type: "security", exchange: instrument.exchange || instrument.mic }
  }
  if (isExperiment) {
    return { ...instrument, type: "experiment" }
  }
  return instrument
}

const getRecentResults = createSelector(
  [getRecentInstruments, getHiddenTypes, getSearchType, getFilterEquitiesFunction, getResourceForExperiments, getSecurityIds],
  (recentInstruments, hiddenTypes, searchType, filterEquities, resourceForExperiments, securityIds) => {
    let instruments = recentInstruments
    if (!searchType || searchType === RECENT_INSTRUMENT_SEARCH_TYPES.GENERAL) {
      instruments = instruments.filter(instrument => instrument.searchType === RECENT_INSTRUMENT_SEARCH_TYPES.GENERAL)
    } else {
      instruments = instruments.filter(instrument => instrument.searchType === searchType)
    }

    // If we are using recent instruments and we are within the context of a portfolio or composite asset,
    // we need to make sure the recent instrument exists within that context
    if (securityIds && searchType === RECENT_INSTRUMENT_SEARCH_TYPES.FILTER) {
      const { modelProviderIds, swapTickers } = securityIds
      const securityKeys = ([...modelProviderIds, ...swapTickers].reduce((acc, key) => {
        acc[key] = true
        return acc
      }, {}))
      instruments = instruments.filter(instrument => securityKeys[instrument.modelProviderId] || securityKeys[instrument.ticker])
    }

    const sortedAndLimitedInstruments = chain(instruments)
      .slice(0, RECENT_RESULTS_LIMIT)
      .orderBy("order")
      .value()

    const results = sortedAndLimitedInstruments.reduce((acc, instrument) => {
      const typeInfo = determineInstrumentType(instrument, resourceForExperiments)

      // Skip instruments that should be filtered out
      if (!typeInfo) {
        return acc
      }

      const { type, isEquity, isBasket, isExperiment } = typeInfo
      const pluralType = pluralize(type)
      const newInstrument = computeNewInstrument(instrument, isEquity, isBasket, isExperiment)

      if (isBasket) {
        newInstrument.exchange = "_SWP"
        newInstrument.name = newInstrument.name?.toUpperCase()
        newInstrument.ticker = newInstrument.name
        newInstrument.assetClass = "swap"
      }

      if (!acc[pluralType]) {
        acc[pluralType] = []
      }
      acc[pluralType].push(newInstrument)

      return acc
    }, {})

    for (const [key, value] of Object.entries(results)) {
      results[`${key}Count`] = size(value)
    }
    return computeSearchResults(results, hiddenTypes, filterEquities)
  },
)

const getResults = createSelector(
  [getRawResults, getHiddenTypes, getFilterEquitiesFunction, getRecentResults],
  computeSearchResults,
)

const getUsingRecentResults = createSelector(
  [getRawResults, getRecentResults],
  (results, recentResults) => !results && !!recentResults,
)

const getAggregateTab = createSelector(
  [getUsingRecentResults],
  (usingRecentResults) => usingRecentResults ? "recent" : "all",
)

export const getModelId = createSelector(
  [getPropsModelId, getRawModelId],
  (propsModelId, modelId) => modelId || propsModelId,
)

function generateTabs(results, aggregateTab) {
  if (!results) {
    return null
  }

  const { equitiesCount = 0, currenciesCount = 0 } = results
  const portfoliosCount = size(results.portfolios)
  const swapsCount = size(results.swaps)
  const watchlistsCount = size(results.watchlists)
  const factorsCount = size(results.factors)
  const experimentsCount = size(results.experiments)
  const benchmarksCount = size(results.benchmarks)
  const indexesCount = size(results.indexes)
  const researchTopicsCount = size(results.researchTopics)

  const totalCount = sum([
    portfoliosCount,
    swapsCount,
    watchlistsCount,
    factorsCount,
    experimentsCount,
    benchmarksCount,
    indexesCount,
    equitiesCount,
    currenciesCount,
    researchTopicsCount,
  ])

  if (totalCount === 0) {
    return null
  }

  return chain([{
    id: aggregateTab,
    count: totalCount,
    disabled: false,
  }, {
    id: "portfolios",
    count: portfoliosCount,
    disabled: portfoliosCount === 0,
  }, {
    id: "baskets",
    count: swapsCount,
    disabled: swapsCount === 0,
  }, {
    id: "watchlists",
    count: watchlistsCount,
    disabled: watchlistsCount === 0,
  }, {
    id: "securities",
    count: equitiesCount,
    disabled: equitiesCount === 0,
  }, {
    id: "currencies",
    count: currenciesCount,
    disabled: currenciesCount === 0,
  }, {
    id: "benchmarks",
    count: benchmarksCount,
    disabled: benchmarksCount === 0,
  }, {
    id: "indexes",
    count: indexesCount,
    disabled: indexesCount === 0,
  }, {
    id: "experiments",
    count: experimentsCount,
    disabled: experimentsCount === 0,
  }, {
    id: "factors",
    count: factorsCount,
    disabled: factorsCount === 0,
  }, {
    id: "researchTopics",
    count: researchTopicsCount,
    disabled: researchTopicsCount === 0,
  }])
    .filter(({ disabled }) => !disabled)
    .value()
}

export const getTabs = createSelector(
  [getResults, getAggregateTab],
  generateTabs,
)

export const getSelectedTab = createSelector(
  [getTabs, getRawSelectedTab, getAggregateTab],
  (tabs, selectedTab, aggregateTab) => {
    if (!tabs) {
      return aggregateTab
    }

    if (chain(tabs).find(t => t.id === selectedTab).get("disabled", true).value()) {
      return aggregateTab
    }

    return selectedTab
  },
)

const getItemsRaw = createSelector(
  [getResults, getAggregateTab, getSelectedTab],
  (results, aggregateTab, selectedTab) => {
    const nbResults = selectedTab === aggregateTab ? 12 : 50

    const tabValues = (tab, key = tab) => {
      if (selectedTab === aggregateTab || selectedTab === tab) {
        return chain(results).get(key).take(nbResults).value()
      }
      return EMPTY_ARRAY
    }

    let equities = tabValues("securities", "equities")
    let currencies = tabValues("currencies", "currencies")
    let swaps = tabValues("baskets", "swaps")
    let benchmarks = tabValues("benchmarks")
    let indexes = tabValues("indexes")
    let portfolios = tabValues("portfolios")
    let experiments = tabValues("experiments")
    let watchlists = tabValues("watchlists")
    let factors = tabValues("factors")
    let researchTopics = tabValues("researchTopics")

    if (selectedTab === aggregateTab) {
      const counts = {
        equities: size(equities),
        currencies: size(currencies),
        swaps: size(swaps),
        benchmarkCount: size(benchmarks),
        indexes: size(indexes),
        portfolios: size(portfolios),
        experiments: size(experiments),
        watchlists: size(watchlists),
        factor: size(factors),
        researchTopics: size(researchTopics),
      }

      const toTake = (tab) => Math.max(3, nbResults - chain(counts)
        .map((value, key) => key === tab ? 0 : value)
        .sum()
        .value())

      swaps = take(swaps, toTake("swaps"))
      benchmarks = take(benchmarks, toTake("benchmarks"))
      indexes = take(indexes, toTake("indexes"))
      portfolios = take(portfolios, toTake("portfolios"))
      equities = take(equities, toTake("equities"))
      currencies = take(currencies, toTake("currencies"))
      experiments = take(experiments, toTake("experiments"))
      watchlists = take(watchlists, toTake("watchlists"))
      factors = take(factors, toTake("factors"))
      researchTopics = take(researchTopics, toTake("researchTopics"))
    }

    return [
      ...equities,
      ...currencies,
      ...swaps,
      ...portfolios,
      ...benchmarks,
      ...indexes,
      ...experiments,
      ...watchlists,
      ...factors,
      ...researchTopics,
    ]
  },
)

const getIsSecondaryActionOpenComposite = (actions) => {
  if (actions?.secondaryAction) {
    return actions.secondaryActionType === CompositeTypes.OPEN_COMPOSITE_SECURITY
  }
  return false
}

const getModelSupportsComposites = createSelector(
  [getMissingDataFiles, getModelId],
  modelSupportsComposites,
)

const getItemsWithoutSelected = createSelector(
  [getItemsRaw, getItemsActions, getFavoriteFactors, getModelSupportsComposites],
  (items, itemsActions, favoriteFactors, modelSupportsComposites) => {
    const itemSet = new Set()
    return chain(items)
      .map((item) => {
        const itemType = toLower(item.type)
        const itemActions = itemsActions[itemType]
        const actions = mapValues(itemActions || {}, action => isFunction(action) ? action(item) : action)
        const isETF = item.assetSubclass === "etf"
        const isETFWithMissingConstituents = isETF && (!modelSupportsComposites || !item.hasComposition)

        let secondaryTooltip = ""
        const isSecondaryActionOpenComposite = getIsSecondaryActionOpenComposite(actions)
        if (isSecondaryActionOpenComposite) {
          secondaryTooltip = getCompositeSecurityTooltip(item, { modelSupportsComposites })
        }

        const commonProps = ["id", "name", "order"]
        const securityId = getSecurityId(item)

        switch (itemType) {
          case "benchmark": {
            const key = `benchmark:${item.alias || item.id}`
            if (itemSet.has(key)) {
              return
            }
            itemSet.add(key)
            return {
              ...pick(item, commonProps),
              type: "benchmark",
              description: item.name,
              detail: item.description,
              secondary: "Benchmark",
              // [TODO] [#184018795] JSX shouldn't be in selectors or models
              icon: <Tonality />,
              iconType: "benchmark",
              secondaryIcon: "VS",
              ...actions,
            }
          }
          case "index": {
            const key = `index:${item.indexId || item.id}`
            if (itemSet.has(key)) {
              return
            }
            itemSet.add(key)
            return {
              ...pick(item, ["assetClass", ...commonProps]),
              type: "index",
              description: item.name,
              secondary: "Index",
              detail: item.description,
              icon: <Tonality />,
              iconType: "benchmark",
              secondaryIcon: isSecondaryActionOpenComposite ? <OpenInNew /> : "VS",
              secondaryTooltip: isSecondaryActionOpenComposite ? secondaryTooltip : "",
              ...actions,
            }
          }
          case "portfolio": {
            const key = `portfolio:${item.alias || item.id}`
            if (itemSet.has(key)) {
              return
            }
            itemSet.add(key)
            return {
              ...pick(item, ["alias", ...commonProps]),
              type: "portfolio",
              description: item.name,
              detail: item.description,
              secondary: "Portfolio",
              icon: <Work />,
              iconType: "portfolio",
              secondaryIcon: "VS",
              ...actions,
            }
          }
          case "experiment": {
            const key = `experiment:${item.alias || item.id}`
            if (itemSet.has(key)) {
              return
            }
            itemSet.add(key)
            return {
              ...pick(item, commonProps),
              type: "experiment",
              description: item.name,
              detail: item.description,
              secondary: "Experiment",
              icon: <BlurOn />,
              iconType: "experiment",
              secondaryIcon: "VS",
              ...actions,
            }
          }
          case "watchlist": {
            const key = `watchlist:${item.alias || item.id}`
            if (itemSet.has(key)) {
              return
            }
            itemSet.add(key)
            return {
              ...pick(item, commonProps),
              type: "watchlist",
              description: item.name,
              detail: item.description,
              secondary: "Watchlist",
              icon: <Dns />,
              iconType: "watchlist",
              secondaryIcon: "VS",
              ...actions,
            }
          }
          case "factor": {
            const key = `factor:${item.alias || item.id}`
            if (itemSet.has(key)) {
              return
            }
            itemSet.add(key)
            return {
              ...pick(item, commonProps),
              type: "factor",
              description: item.name,
              secondary: "Factor",
              icon: <Texture />,
              iconType: "factor",
              secondaryActionOnly: true,
              secondaryIcon: favoriteFactors[item.id] ? <Star className="favorite-factor" /> : <StarBorder />,
              ...actions,
            }
          }
          case "researchTopic": {
            const key = `researchTopic:${item.alias || item.id}`
            if (itemSet.has(key)) {
              return
            }
            itemSet.add(key)
            return {
              ...pick(item, commonProps),
              type: "researchTopic",
              description: item.name,
              detail: item.description,
              secondary: "Research Topic",
              icon: <Gradient />,
              iconType: "researchTopic",
              ...actions,
            }
          }
          default: {
            const key = `security:${securityId}`
            if (itemSet.has(key)) {
              return
            }
            itemSet.add(key)
            return {
              ...pick(item, ["internalId", "ticker", "modelProviderId", "bloombergTicker", "figi", "country", "assetClass", "assetSubclass", ...commonProps]),
              type: "security",
              id: securityId,
              resourceId: item.id || item.ticker,
              mic: item.exchange,
              detail: item.description,
              description: item.name,
              secondary: item.country ? `${item.ticker} - ${item.country}` : item.ticker,
              ...getSecurityIcon(item),
              secondaryIcon: isSecondaryActionOpenComposite ? <OpenInNew /> : "VS",
              secondaryTooltip: isSecondaryActionOpenComposite ? secondaryTooltip : "",
              secondaryDisabled: isETFWithMissingConstituents,
              ...actions,
            }
          }
        }
      })
      .compact()
      .value()
  },
)

const getSelected = createSelector(
  [getItemsWithoutSelected, getSelectedRaw],
  (itemsWithoutSelected, selected) => {
    let visibleCount = 0
    if (itemsWithoutSelected) {
      visibleCount = itemsWithoutSelected.length
    }

    const historyItems = []

    // Don't do the modulo thing if we have reset the selected value
    if (selected) {
      selected = selected % (visibleCount + historyItems.length + 1) - 1
      if (selected < -1) {
        selected = visibleCount + historyItems.length + selected + 1
      }
    } else {
      selected = -1
    }

    return selected
  },
)

export const getItems = createSelector(
  [getItemsWithoutSelected, getSelected],
  (itemsWithoutSelected, selected) => {
    return (itemsWithoutSelected || []).map((item, index) => ({
      ...item,
      selected: selected === index,
    }))
  },
)

function getSecurityId(item) {
  return item.assetClass === "currency" ? item.ticker : `${item.ticker}.${item.exchange}`
}

function getSecurityIcon(item) {
  switch (item.assetClass || item.resourceType) {
    case "swap":
      return {
        icon: <ShoppingBasket />,
        iconType: "swap",
      }
    case "currency":
      return {
        icon: <AttachMoney />,
        iconType: "currency",
      }
    default:
      return {
        icon: <SelectAll />,
        iconType: "security",
      }
  }
}

export const getSelectedItem = createSelector(
  [getItems],
  (items) => {
    return find(items, item => item.selected)
  },
)
